import { memo, useCallback } from 'react'

import { useQuery } from '@tanstack/react-query'
import { useLocation } from '@tanstack/react-router'
import { enqueueSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { Header } from '@shared/components/Header'
import { Tabs } from '@shared/components/MaterialUIEnhancements/Tabs'
import { Content, TabContent } from '@shared/layouts'
import { NavigationTabLink } from 'components/LinkedMUIComponents/NavigationTabLink'
import { ActionsMenu, DynamicBadge } from 'domains/ContentProcessing/AvcInstances/Instance'
import { useAvcInstanceUpdateMutation } from 'hooks/mutations/content'
import {
  avioRoute,
  dashboardRoute,
  avcInstanceRoute,
  avcInstanceTasksRoute,
} from 'pages/NWPlatform/AvcSystem/Instances/Instance'
import { avcInstanceQueryKeys } from 'services/queryKeys'

const StructureRaw = () => {
  const { t } = useTranslation(['common', 'pages'])

  const { pathname } = useLocation()
  const { avcInstanceId: instanceId } = avcInstanceRoute.useParams()

  const { mutate: updateEvent, ...avcInstanceUpdateMutation } = useAvcInstanceUpdateMutation()

  const avcInstanceQuery = useQuery({
    ...avcInstanceQueryKeys.detail({ instanceId }),
    select: (data) => ({ name: data.name, health: data.health }),
  })
  const { data: avcInstance } = avcInstanceQuery

  const handleUpdateAvcInstanceName = useCallback(
    (title: string, closeCallback: () => void) => {
      updateEvent(
        {
          instanceId,
          avcInstanceEdit: { name: title },
        },
        {
          onError: () => enqueueSnackbar(t('pages:ContentProcessing.updateError')),
          onSettled: closeCallback,
        },
      )
    },
    [t, updateEvent, instanceId],
  )

  return (
    <Content
      HeaderSlot={
        <Header
          title={avcInstanceQuery.data?.name ?? ''}
          isLoading={avcInstanceQuery.isLoading}
          isSaving={avcInstanceUpdateMutation.isPending}
          onSave={handleUpdateAvcInstanceName}
          appendix={avcInstance && <DynamicBadge health={avcInstance.health} />}
          Actions={<ActionsMenu />}
          TabNavigation={
            <Tabs>
              <NavigationTabLink from={avcInstanceRoute.fullPath} to={dashboardRoute.to}>
                {t('pages:ContentProcessing.dashboard')}
              </NavigationTabLink>
              <NavigationTabLink from={avcInstanceRoute.fullPath} to={avcInstanceTasksRoute.to}>
                {t('pages:ContentProcessing.task', { count: 2 })}
              </NavigationTabLink>
              <NavigationTabLink from={avcInstanceRoute.fullPath} to={avioRoute.to}>
                {t('pages:ContentProcessing.aVIoMapping')}
              </NavigationTabLink>
            </Tabs>
          }
        />
      }
    >
      <TabContent pathname={pathname} />
    </Content>
  )
}

export const Structure = memo(StructureRaw)
